import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Select, TimePicker, Tooltip } from 'antd';
import moment from 'moment';
import { FC, useCallback, useMemo, useState } from 'react';
import { QueueLineModel, ShopperMesh, ShopperType } from '../../../../types';

import largeManWithBasketShopperImage from '../../../../../../../assets/fixtures/BasketMaleLgShopper/front.png';
import smallManWithBasketShopperImage from '../../../../../../../assets/fixtures/BasketMaleSmShopper/front.png';
import basketShopperImage from '../../../../../../../assets/fixtures/BasketShopper/front.png';
import cartShopperImage from '../../../../../../../assets/fixtures/CartShopper/front.png';
import womanWithoutBasketShopperImage from '../../../../../../../assets/fixtures/NoBasketFemaleSmShopper/front.png';
import largeManWithoutBasketShopperImage from '../../../../../../../assets/fixtures/NoBasketMaleLgShopper/front.png';
import smallManWithoutBasketShopperImage from '../../../../../../../assets/fixtures/NoBasketMaleSmShopper/front.png';
import { ReactComponent as CloseIcon } from '../../../../../../../assets/svgs/close-icon.svg';
import ShopperOrderNumber from './components/ShopperOrderNumber';

import './ShopperProperties.less';

const { Option } = Select;

interface ShopperPropertiesProp {
  shopper: ShopperMesh;
  selectedQueueLine: QueueLineModel | undefined;
  overlappingShoppers: string[];
  onUpdateShopper: (
    shopper: ShopperMesh,
    action: 'updateShopperType' | 'removeShopper' | 'updateElapsedWaitTime',
    value?: ShopperType | string
  ) => void;
}

const ShopperProperties: FC<ShopperPropertiesProp> = ({
  shopper,
  selectedQueueLine,
  overlappingShoppers,
  onUpdateShopper,
}) => {
  const [showProperties, setShowProperties] = useState<boolean>(true);

  const toggleShowProperties = () => {
    if ('startViewTransition' in document) {
      (document as any).startViewTransition(() => {
        setShowProperties(!showProperties);
      });
    } else {
      setShowProperties(!showProperties);
    }
  };

  const handleRemoveShopper = useCallback(
    (shopperToRemove: ShopperMesh) => {
      console.log(
        'Removing shopper from properties:',
        shopperToRemove.id,
        shopperToRemove.userData
      );
      onUpdateShopper(shopperToRemove, 'removeShopper');
    },
    [onUpdateShopper]
  );

  const getQueueLineGroupName = useCallback((lineId: number) => {
    return String.fromCharCode(64 + lineId); // Convert 1 to A, 2 to B, etc.
  }, []);

  const isOnlyShopper = useMemo((): boolean => {
    return selectedQueueLine?.shoppers.length === 1;
  }, [selectedQueueLine?.shoppers.length]);

  const isFirstInLine = useCallback(
    (orderInLine: number): boolean => {
      return orderInLine === 1 && !isOnlyShopper;
    },
    [isOnlyShopper]
  );

  const isMiddleInLine = useCallback(
    (orderInLine: number): boolean => {
      return (
        orderInLine > 1 && orderInLine < selectedQueueLine?.shoppers.length!
      );
    },
    [selectedQueueLine?.shoppers.length]
  );

  const isLastInLine = useCallback(
    (orderInLine: number): boolean => {
      return (
        orderInLine === selectedQueueLine?.shoppers.length! && !isOnlyShopper
      );
    },
    [isOnlyShopper, selectedQueueLine?.shoppers.length]
  );

  const sortShoppersFromLeastToGreatest = useCallback(
    (a: ShopperMesh, b: ShopperMesh) => {
      return a.userData.orderInLine - b.userData.orderInLine;
    },
    []
  );

  const isSelectedShopper = useCallback(
    (meshId: number): boolean => {
      return shopper.id === meshId;
    },
    [shopper.id]
  );

  const isOverlapping = useCallback(
    (meshId: number): boolean => {
      return overlappingShoppers.includes(meshId.toString());
    },
    [overlappingShoppers]
  );

  const renderShopperPreviewImage = useCallback((type: ShopperType) => {
    switch (type) {
      case ShopperType.WomanWithBasket:
        return (
          <img
            src={basketShopperImage}
            height={50}
            alt="woman-with-basket-shopper"
          />
        );
      case ShopperType.WomanWithCart:
        return (
          <img
            src={cartShopperImage}
            height={50}
            alt="woman-with-cart-shopper"
          />
        );
      case ShopperType.WomanWithoutBasket:
        return (
          <img
            src={womanWithoutBasketShopperImage}
            height={50}
            alt="woman-without-basket-shopper"
          />
        );
      case ShopperType.SmallManWithBasket:
        return (
          <img
            src={smallManWithBasketShopperImage}
            height={50}
            alt="small-man-with-basket-shopper"
          />
        );
      case ShopperType.SmallManWithoutBasket:
        return (
          <img
            src={smallManWithoutBasketShopperImage}
            height={50}
            alt="small-man-without-basket-shopper"
          />
        );
      case ShopperType.LargeManWithBasket:
        return (
          <img
            src={largeManWithBasketShopperImage}
            height={50}
            alt="large-man-with-basket-shopper"
          />
        );
      case ShopperType.LargeManWithoutBasket:
        return (
          <img
            src={largeManWithoutBasketShopperImage}
            height={50}
            alt="large-man-without-basket-shopper"
          />
        );
      default:
        return (
          <img
            src={basketShopperImage}
            height={50}
            alt="woman-with-basket-shopper"
          />
        );
    }
  }, []);

  return (
    <section className="shopper-properties-section">
      <div className="shopper-properties-header">
        <div className="shopper-properties-header__section-left">
          <h1>Properties</h1>
        </div>
        <div className="shopper-properties-header__section-right">
          {showProperties ? (
            <Tooltip title="Hide" mouseEnterDelay={0.5}>
              <MinusOutlined
                onClick={toggleShowProperties}
                className="shopper-properties-icon"
              />
            </Tooltip>
          ) : (
            <Tooltip title="Show" mouseEnterDelay={0.5}>
              <PlusOutlined
                onClick={toggleShowProperties}
                className="shopper-properties-icon"
              />
            </Tooltip>
          )}
        </div>
      </div>
      <div
        className={`shopper-properties-panel ${
          showProperties ? 'visible' : 'hidden'
        }`}
      >
        <div className="shoppers-queue-line-wrapper">
          <div
            className="queue-line-header"
            style={{ background: selectedQueueLine?.color }}
          >
            <div>{`Queue line ${getQueueLineGroupName(
              selectedQueueLine?.lineId!
            )} - Wait Time Per Shopper`}</div>
            <TimePicker
              showNow={false}
              style={{ width: '95px' }}
              onChange={(_, timeString) =>
                onUpdateShopper(
                  shopper,
                  'updateElapsedWaitTime',
                  timeString || '01:00'
                )
              }
              value={moment(
                shopper.userData.elapsedWaitTime || '01:00',
                'mm:ss'
              )}
              format={'mm:ss'}
            />
            <div
              className="queue-line-tool-info"
              style={{ color: selectedQueueLine?.color }}
              title="The queue line tool allows you to create a queue line by placing shoppers. The first shopper will disappear after the wait time passes, and each shopper will move up in line."
            >
              ?
            </div>
          </div>

          <div
            className="queue-line-shoppers-list-wrapper"
            style={{ maxHeight: '60vh', overflowY: 'auto' }}
          >
            {selectedQueueLine?.shoppers
              .sort(sortShoppersFromLeastToGreatest)
              .map((shopperMesh: ShopperMesh) => {
                return (
                  <div className="queue-line-shopper-item-wrapper">
                    <ShopperOrderNumber
                      shopperMesh={shopperMesh}
                      selectedQueueLineColor={selectedQueueLine?.color || ''}
                      isSelectedShopper={isSelectedShopper(shopperMesh.id)}
                      isOverlapping={isOverlapping(shopperMesh.id)}
                      isFirstInLine={isFirstInLine(
                        shopperMesh.userData.orderInLine
                      )}
                      isMiddleInLine={isMiddleInLine(
                        shopperMesh.userData.orderInLine
                      )}
                      isLastInLine={isLastInLine(
                        shopperMesh.userData.orderInLine
                      )}
                    />
                    <div
                      key={shopperMesh.id}
                      className="queue-line-shopper-item"
                      style={{ borderColor: selectedQueueLine?.color }}
                    >
                      <div className="shopper-actions">
                        <div className="shopper-type-image-wrapper">
                          {renderShopperPreviewImage(shopperMesh.userData.type)}
                        </div>
                        <div className="shopper-dropdown-action-label">
                          Shopper:
                        </div>
                        <Select
                          size="small"
                          value={shopperMesh.userData.type}
                          onChange={(value) =>
                            onUpdateShopper(
                              shopperMesh,
                              'updateShopperType',
                              value
                            )
                          }
                          style={{ width: '126px', textAlign: 'center' }}
                        >
                          <Option
                            key={ShopperType.WomanWithBasket}
                            value={ShopperType.WomanWithBasket}
                          >
                            Woman, Basket
                          </Option>
                          <Option
                            key={ShopperType.WomanWithCart}
                            value={ShopperType.WomanWithCart}
                          >
                            Woman, Cart
                          </Option>
                        </Select>
                      </div>
                      <div
                        className="remove-shopper-icon-wrapper"
                        onClick={() => handleRemoveShopper(shopperMesh)}
                      >
                        <CloseIcon />
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ShopperProperties;
